import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';

import {
  CreateProviderDTO,
  ProviderControllerService,
  ProviderResponseDTO,
} from '@app/api/einvoice-service';

export interface EInvoiceProvider {
  id: number;
  providerName: string;
  providerCode: string;
  note?: string;
  status: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface EInvoiceProviderPageRequest {
  providerName?: string;
  providerCode?: string;
  status?: boolean;
  page: number;
  size: number;
}

export interface EInvoiceProviderPageResponse {
  pageContent: EInvoiceProvider[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

export interface EInvoiceProviderCreateRequest {
  providerName: string;
  providerCode: string;
  note?: string;
  status: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class EInvoiceProviderService {
  private readonly apiService = inject(ProviderControllerService);
  private readonly providerCodeById = new Map<number, string>();

  getProviderCodeById(id: number): string | undefined {
    return this.providerCodeById.get(id);
  }

  private mapToProvider(dto: ProviderResponseDTO): EInvoiceProvider {
    return {
      id: dto.id ?? 0,
      providerName: dto.name ?? '',
      providerCode: dto.code ?? '',
      note: dto.description ?? '',
      status: dto.status !== false,
    };
  }

  private mapToCreateDTO(
    payload: Partial<EInvoiceProviderCreateRequest>,
  ): CreateProviderDTO {
    const dto: CreateProviderDTO = {};
    if (payload.providerName !== undefined) dto.name = payload.providerName;
    if (payload.providerCode !== undefined) dto.code = payload.providerCode;
    if (payload.note !== undefined) dto.description = payload.note;
    if (payload.status !== undefined) {
      dto.status = payload.status;
    }
    return dto;
  }

  getProviderOptionsPage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<number>> {
    const searchText = params.searchText?.trim();
    return this.searchProviders({
      page: params.page - 1,
      size: params.size,
      providerName: searchText || undefined,
      providerCode: searchText || undefined,
    }).pipe(
      map((response) => {
        response.pageContent.forEach((p) => {
          this.providerCodeById.set(p.id, p.providerCode);
        });
        return {
          options: response.pageContent.map((p) => ({
            label: p.providerName || p.providerCode,
            value: p.id,
          })),
          pageContentLength: response.pageContent.length,
          totalElements: response.totalElements,
        };
      }),
      catchError(() => of({ options: [], pageContentLength: 0 })),
    );
  }

  searchProviders(
    payload: EInvoiceProviderPageRequest,
  ): Observable<EInvoiceProviderPageResponse> {
    return this.apiService
      .getProviders(
        payload.providerCode,
        payload.providerName,
        payload.status,
        payload.page,
        payload.size,
        undefined,
        undefined,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        map((response) => ({
          pageContent: (response.pageContent || []).map((d) =>
            this.mapToProvider(d),
          ),
          totalElements: response.totalElements || 0,
          totalPages: response.totalPages || 0,
          page: response.number || 0,
          size: response.size || payload.size,
        })),
      );
  }

  getProviderById(id: number): Observable<EInvoiceProvider> {
    return this.apiService
      .getProvider(id, undefined, undefined, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(map((dto) => this.mapToProvider(dto)));
  }

  /**
   * Creates a new e-invoice provider.
   */
  createProvider(payload: EInvoiceProviderCreateRequest): Observable<object> {
    return this.apiService.createProvider(
      this.mapToCreateDTO(payload),
      undefined,
      undefined,
      { httpHeaderAccept: 'text/plain' as '*/*' },
    );
  }

  /**
   * Updates an existing provider. Accepts a partial payload —
   * only the provided fields will be sent to the API.
   */
  updateProvider(
    id: number,
    payload: EInvoiceProviderCreateRequest,
  ): Observable<object> {
    return this.apiService.updateProvider(
      id,
      this.mapToCreateDTO(payload),
      undefined,
      undefined,
      { httpHeaderAccept: 'text/plain' as '*/*' },
    );
  }

  deleteProvider(id: number): Observable<object> {
    return this.apiService.deleteProvider(id, undefined, undefined, {
      httpHeaderAccept: 'text/plain' as '*/*',
    });
  }
}
